import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import Button from '../components/ui/Button';
import { db } from '../lib/db';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { 
  FaGlobe, FaFacebook, FaInstagram, FaWhatsapp, FaGoogle, FaLinkedin, FaTwitter, 
  FaYoutube, FaGithub, FaStar, FaAlignLeft, FaWifi, FaAddressCard, FaCalendarAlt, 
  FaCopy, FaCheck, FaDownload, FaMapMarkerAlt, FaClock
} from 'react-icons/fa';

const { FiLoader, FiAlertOctagon, FiExternalLink, FiArrowRight, FiCheck: FiCheckIcon } = FiIcons;

const detectType = (data) => {
  if (!data) return 'empty';
  const cleanData = data.trim();
  if (cleanData.startsWith('text:')) return 'text';
  if (cleanData.startsWith('WIFI:')) return 'wifi';
  if (cleanData.includes('BEGIN:VCARD')) return 'vcard';
  if (cleanData.includes('BEGIN:VCALENDAR')) return 'event';
  if (cleanData.startsWith('tel:')) return 'phone';
  if (cleanData.startsWith('sms:')) return 'sms';
  if (cleanData.startsWith('mailto:')) return 'email';
  if (cleanData.includes('wa.me')) return 'whatsapp';
  if (cleanData.includes('maps.google') || cleanData.startsWith('geo:')) return 'location';
  return 'url';
};

const TagRouter = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  
  // Render States
  const [directDest, setDirectDest] = useState(null);
  const [specialData, setSpecialData] = useState(null); // {type, content}
  const [profileData, setProfileData] = useState(null);
  const [copied, setCopied] = useState(false);

  // Review Filter State
  const [reviewMode, setReviewMode] = useState(null);
  const [rating, setRating] = useState(0);
  const [reviewConfig, setReviewConfig] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [userName, setUserName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentTag, setCurrentTag] = useState(null);

  useEffect(() => {
    const handleRoute = async () => {
      const tagCode = searchParams.get('tag_id');
      if (!tagCode) {
        setError("Invalid card: Missing Tag ID.");
        return;
      }

      try {
        let record = await db.findByTagId(tagCode);
        
        if (!record) {
          record = await db.create(tagCode);
          if (record) navigate('/setup', { state: { ...record } });
          else setError("Could not register this tag.");
          return;
        }

        if (record.is_active !== 1) {
          navigate('/setup', { state: { ...record } });
          return;
        }

        setCurrentTag(record);
        db.incrementScan(record);

        // Review Filter
        if (record.negative_review_filter_enabled === 1 && record.google_review_link) {
          setReviewConfig({
            googleLink: record.google_review_link,
            dummyMessage: record.dummy_page_message || "Thank you for feedback!"
          });
          setReviewMode('rating');
          return;
        }

        // Smart Profile
        if (record.link_type === 'smart') {
          const links = await db.getProfileLinks(tagCode);
          setProfileData({
            company: record.company_name,
            logoUrl: record.logo_url,
            bio: record.bio,
            links: links
          });
          return;
        }

        // Direct Link Handling
        const rawUrl = record.redirect_url || '';
        const type = detectType(rawUrl);

        if (['text', 'wifi', 'vcard', 'event'].includes(type)) {
          // Special Display Types
          setSpecialData({ type, content: rawUrl });
        } else {
          // Redirect Types (URL, Phone, SMS, Email, Maps)
          let finalUrl = rawUrl;
          if (type === 'url' && !finalUrl.startsWith('http')) {
            finalUrl = 'https://' + finalUrl;
          }
          if (window.self !== window.top) {
            setDirectDest(finalUrl); // Iframe detection
          } else {
            window.location.replace(finalUrl);
          }
        }
      } catch (err) {
        console.error("Router error:", err);
        setError("Network error.");
      }
    };

    handleRoute();
  }, [searchParams, navigate]);

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadFile = (data, filename, mimeType) => {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const formatIcsDate = (icsDate) => {
    if (!icsDate) return '';
    try {
      const y = icsDate.substring(0, 4);
      const m = parseInt(icsDate.substring(4, 6)) - 1;
      const d = icsDate.substring(6, 8);
      const h = icsDate.substring(9, 11);
      const min = icsDate.substring(11, 13);
      // Assuming 'Z' indicates UTC
      const date = new Date(Date.UTC(y, m, d, h, min));
      
      return date.toLocaleString('en-US', { 
        weekday: 'short', 
        year: 'numeric', 
        month: 'short', 
        day: 'numeric', 
        hour: 'numeric', 
        minute: '2-digit' 
      });
    } catch (e) {
      return icsDate;
    }
  };

  // --- RENDER: SPECIAL CARDS (Text, WiFi, vCard, Event) ---
  if (specialData) {
    const { type, content } = specialData;
    return (
      <Layout hideActions={true}>
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-6 animate-in fade-in slide-in-from-bottom duration-500">
          
          {type === 'text' && (
            <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6">
              <div className="w-16 h-16 bg-zinc-800 rounded-2xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FaAlignLeft} className="text-3xl text-zinc-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold mb-4">Message</h2>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">
                  {content.substring(5)}
                </p>
              </div>
            </div>
          )}

          {type === 'wifi' && (
            <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6">
              <div className="w-16 h-16 bg-blue-500/20 rounded-2xl flex items-center justify-center mx-auto">
                <SafeIcon icon={FaWifi} className="text-3xl text-blue-500" />
              </div>
              <h2 className="text-2xl font-bold">Join Network</h2>
              {(() => {
                const parts = content.split(';');
                const ssid = parts.find(p => p.startsWith('S:'))?.split(':')[1];
                const pass = parts.find(p => p.startsWith('P:'))?.split(':')[1];
                return (
                  <div className="space-y-4">
                    <div className="bg-zinc-950 rounded-xl p-4">
                      <p className="text-xs text-zinc-500 uppercase">Network</p>
                      <p className="text-xl font-bold">{ssid}</p>
                    </div>
                    {pass && (
                      <div className="bg-zinc-950 rounded-xl p-4 flex items-center justify-between">
                        <div className="text-left">
                          <p className="text-xs text-zinc-500 uppercase">Password</p>
                          <p className="font-mono text-lg">{pass}</p>
                        </div>
                        <button onClick={() => handleCopy(pass)} className="p-2 text-zinc-400 hover:text-white">
                          <SafeIcon icon={copied ? FaCheck : FaCopy} />
                        </button>
                      </div>
                    )}
                    <p className="text-xs text-zinc-500">Copy the password and connect in settings.</p>
                  </div>
                );
              })()}
            </div>
          )}

          {type === 'vcard' && (
            <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl p-8 text-center space-y-6">
              <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto">
                <SafeIcon icon={FaAddressCard} className="text-4xl text-orange-500" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Contact Card</h2>
                <p className="text-zinc-400 mt-2">Save this contact to your phone</p>
              </div>
              <Button onClick={() => downloadFile(content, 'contact.vcf', 'text/vcard')} className="py-4">
                <SafeIcon icon={FaDownload} /> Save Contact
              </Button>
            </div>
          )}

          {type === 'event' && (
            <div className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden shadow-2xl shadow-purple-900/20">
              <div className="bg-purple-600/10 p-8 text-center border-b border-purple-500/20">
                <div className="w-20 h-20 bg-purple-500/20 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <SafeIcon icon={FaCalendarAlt} className="text-4xl text-purple-500" />
                </div>
                {(() => {
                  const getVal = (key) => {
                     const match = content.match(new RegExp(`${key}:(.*)`));
                     return match ? match[1].trim() : '';
                  };
                  const title = getVal('SUMMARY');
                  return <h2 className="text-2xl font-bold text-white">{title || 'Event Invitation'}</h2>;
                })()}
              </div>
              
              <div className="p-6 space-y-6">
                {(() => {
                  const getVal = (key) => {
                     const match = content.match(new RegExp(`${key}:(.*)`));
                     return match ? match[1].trim() : '';
                  };
                  const startStr = getVal('DTSTART');
                  const endStr = getVal('DTEND');
                  const loc = getVal('LOCATION');
                  const desc = getVal('DESCRIPTION');

                  return (
                    <>
                      <div className="space-y-4">
                        {startStr && (
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                              <SafeIcon icon={FaClock} className="text-zinc-400" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Start</p>
                              <p className="text-white font-medium">{formatIcsDate(startStr)}</p>
                            </div>
                          </div>
                        )}
                        
                        {endStr && (
                          <div className="flex items-start gap-4">
                             <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                              <SafeIcon icon={FiCheckIcon} className="text-zinc-400" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-zinc-500 uppercase font-bold tracking-wider">End</p>
                              <p className="text-white font-medium">{formatIcsDate(endStr)}</p>
                            </div>
                          </div>
                        )}

                        {loc && (
                          <div className="flex items-start gap-4">
                            <div className="w-10 h-10 bg-zinc-800 rounded-lg flex items-center justify-center flex-shrink-0">
                              <SafeIcon icon={FaMapMarkerAlt} className="text-zinc-400" />
                            </div>
                            <div className="flex-1">
                              <p className="text-xs text-zinc-500 uppercase font-bold tracking-wider">Location</p>
                              <p className="text-white font-medium">{loc}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {desc && (
                        <div className="bg-zinc-950/50 p-4 rounded-xl border border-zinc-800">
                          <p className="text-zinc-400 text-sm leading-relaxed whitespace-pre-wrap">{desc}</p>
                        </div>
                      )}
                    </>
                  );
                })()}

                <Button onClick={() => downloadFile(content, 'event.ics', 'text/calendar')} className="w-full py-4 bg-purple-600 hover:bg-purple-500 shadow-lg shadow-purple-900/20">
                  <SafeIcon icon={FaDownload} /> Add to Calendar
                </Button>
              </div>
            </div>
          )}

        </div>
      </Layout>
    );
  }

  // --- RENDER: REVIEW, PROFILE, ERROR, REDIRECT (Same as before) ---
  if (error) return <Layout hideActions={true}><div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-4 px-4"><SafeIcon icon={FiAlertOctagon} className="text-3xl text-red-500" /><h2 className="text-2xl font-bold">Error</h2><p className="text-zinc-400">{error}</p></div></Layout>;

  if (reviewMode) {
    /* Review UI Code */
    return (
      <div className="min-h-screen bg-white text-zinc-900 font-sans flex flex-col items-center justify-center p-6">
        <div className="w-full max-w-md space-y-8 text-center animate-in fade-in slide-in-from-bottom-8 duration-500">
          <div className="w-16 h-16 bg-amber-500 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/20 transform -rotate-3">
             <SafeIcon icon={FaStar} className="text-3xl text-white" />
          </div>
          {reviewMode === 'rating' && (
            <div className="space-y-8">
              <div><h1 className="text-2xl font-bold mb-2">How was your experience?</h1><p className="text-zinc-500">Tap a star to rate us</p></div>
              <div className="flex justify-center gap-2">
                {[1, 2, 3, 4, 5].map(star => (
                  <button key={star} onClick={() => {
                    setRating(star);
                    if (star >= 4) {
                      setReviewMode('redirecting');
                      setTimeout(() => window.location.replace(reviewConfig.googleLink), 1500);
                    } else setReviewMode('feedback');
                  }} className="text-4xl transition-transform hover:scale-110"><SafeIcon icon={FaStar} className={star <= rating ? "text-amber-400" : "text-zinc-200"} /></button>
                ))}
              </div>
            </div>
          )}
          {reviewMode === 'redirecting' && (
             <div className="space-y-6"><div className="w-16 h-16 border-4 border-amber-100 border-t-amber-500 rounded-full animate-spin mx-auto" /><div><h2 className="text-xl font-bold">Thank you!</h2><p className="text-zinc-500">Redirecting...</p></div></div>
          )}
          {reviewMode === 'feedback' && (
             <div className="space-y-6 text-left"><div className="text-center"><h2 className="text-xl font-bold">We're sorry</h2><p className="text-zinc-500 text-sm">Tell us more.</p></div>
             <div className="space-y-4">
               <input type="text" className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3" placeholder="Name" value={userName} onChange={e => setUserName(e.target.value)} />
               <textarea className="w-full bg-zinc-50 border border-zinc-200 rounded-xl px-4 py-3 min-h-[120px]" placeholder="Feedback..." value={feedback} onChange={e => setFeedback(e.target.value)} />
               <Button onClick={async () => {
                 setIsSubmitting(true);
                 await db.createNegativeReview({ tag_code: currentTag.tag_code, rating, feedback, user_name: userName });
                 setReviewMode('thanks');
                 setIsSubmitting(false);
               }} disabled={!feedback.trim() || isSubmitting} className="bg-amber-500 text-white">{isSubmitting ? "Sending..." : "Submit"}</Button>
             </div></div>
          )}
          {reviewMode === 'thanks' && (
             <div className="space-y-6"><div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto"><SafeIcon icon={FiCheckIcon} className="text-4xl text-green-500" /></div><div><h2 className="text-xl font-bold">Feedback Sent</h2><p className="text-zinc-500 mt-2">{reviewConfig.dummyMessage}</p></div></div>
          )}
        </div>
      </div>
    );
  }

  if (profileData) {
    /* Smart Profile UI */
    return (
      <Layout hideActions={true}>
        <div className="min-h-[70vh] flex flex-col items-center pt-8 animate-in fade-in slide-in-from-bottom-8 duration-500">
          <div className="text-center space-y-4 mb-8">
            <div className="w-24 h-24 bg-gradient-to-br from-red-600 to-zinc-800 rounded-full flex items-center justify-center mx-auto shadow-2xl shadow-red-900/30 border-4 border-black overflow-hidden relative">
              {profileData.logoUrl ? <img src={profileData.logoUrl} alt="Logo" className="w-full h-full object-cover" /> : <span className="text-3xl font-bold text-white uppercase">{profileData.company?.charAt(0)}</span>}
            </div>
            <div><h1 className="text-2xl font-bold text-white">{profileData.company}</h1>{profileData.bio && <p className="text-zinc-400 text-sm mt-1 max-w-xs mx-auto">{profileData.bio}</p>}</div>
          </div>
          <div className="w-full max-w-sm space-y-4 px-4 pb-12">
            {profileData.links.map((link, i) => (
              <a key={i} href={link.url} target="_blank" rel="noreferrer" className="block w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-red-500/50 transition-all p-4 rounded-xl flex items-center gap-4 group">
                <div className="w-10 h-10 bg-zinc-950 rounded-full flex items-center justify-center text-zinc-400 group-hover:text-red-500 transition-colors"><SafeIcon icon={FaGlobe} size={20} /></div>
                <span className="font-semibold text-zinc-200 group-hover:text-white flex-1 text-center pr-10">{link.label}</span>
              </a>
            ))}
          </div>
        </div>
      </Layout>
    );
  }

  if (directDest) {
    return <Layout hideActions={true}><div className="flex flex-col items-center justify-center min-h-[60vh] text-center space-y-8 animate-in fade-in"><div className="w-20 h-20 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center mx-auto"><SafeIcon icon={FiExternalLink} className="text-4xl text-white" /></div><div><h2 className="text-2xl font-bold">Opening...</h2><p className="text-zinc-400 mt-2">Destination: <span className="text-white">{directDest}</span></p></div><Button onClick={() => window.open(directDest, '_blank')} className="w-full max-w-xs py-4">Open Now <SafeIcon icon={FiArrowRight} /></Button></div></Layout>;
  }

  return <Layout hideActions={true}><div className="flex flex-col items-center justify-center min-h-[60vh] space-y-6"><div className="w-16 h-16 border-4 border-zinc-800 border-t-red-600 rounded-full animate-spin" /><p className="text-zinc-400 animate-pulse">Loading...</p></div></Layout>;
};

export default TagRouter;